import "./frontend.css";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import useIsMobile from "../../fx-video-player/src/hooks/isMobile";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Pause, Play } from "./icons";

gsap.registerPlugin(ScrollTrigger);

const divsToUpdate = document.querySelectorAll(".fx-video-player-render");

divsToUpdate.forEach((div) => {
  const data = JSON.parse(div.querySelector("pre").innerText);
  const root = ReactDOM.createRoot(div);
  root.render(<FXVideoPlayer {...data} />);
  div.classList.remove("fx-video-player-render");
});

function FXVideoPlayer(props) {
  const { videoUrlDT, imgFallback } = props;
  const videoRef = useRef(null);
  const scrubBarRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hover, setHover] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const { isMobile } = useIsMobile();

  useEffect(() => {
    const vid = videoRef.current;
    const container = videoContainerRef.current;

    if (!vid || !container) return;

    // GSAP ScrollTrigger to handle visibility-based playback
    ScrollTrigger.create({
      trigger: container,
      start: "top 50%", // Trigger when the top of the video reaches 50% of the viewport
      end: "bottom 50%", // End when the bottom of the video leaves 50% of the viewport
      onEnter: () => {
        vid
          .play()
          .then(() => setIsPlaying(true))
          .catch((err) => console.warn("Autoplay error:", err));
      },
      onLeave: () => {
        vid.pause();
        setIsPlaying(false);
      },
      onEnterBack: () => {
        vid
          .play()
          .then(() => setIsPlaying(true))
          .catch((err) => console.warn("Autoplay error:", err));
      },
      onLeaveBack: () => {
        vid.pause();
        setIsPlaying(false);
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []); // No dependencies since this should only run once

  useEffect(() => {
    const vid = videoRef.current;

    if (!vid) return;

    // Progress tracking
    const updateProgress = () => {
      if (!isDragging) {
        setProgress((vid.currentTime / vid.duration) * 100);
      }
    };

    vid.addEventListener("timeupdate", updateProgress);

    return () => {
      vid.removeEventListener("timeupdate", updateProgress);
    };
  }, [isDragging]); // Only depends on isDragging

  useEffect(() => {
    const vid = videoRef.current;

    if (!vid) return;

    // Reset video and scrub bar when the video ends
    const handleVideoEnd = () => {
      vid.currentTime = 0; // Reset video to the beginning
      setProgress(0); // Reset progress bar
      setIsPlaying(false); // Update play state
    };

    vid.addEventListener("ended", handleVideoEnd);

    return () => {
      vid.removeEventListener("ended", handleVideoEnd);
    };
  }, []);

  const togglePlay = () => {
    const vid = videoRef.current;

    if (vid) {
      if (vid.currentTime === vid.duration) {
        // If the video is at the end, reset it
        vid.currentTime = 0;
        setProgress(0);
      }

      if (vid.paused) {
        vid.play();
        setIsPlaying(true);
      } else {
        vid.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleScrubStart = (event) => {
    const vid = videoRef.current;
    if (!vid || vid.readyState < 1) {
      console.warn("Video not ready");
      return;
    }

    if (isPlaying) {
      vid.pause();
      setIsPlaying(false);
    }

    setIsDragging(true);
    handleScrub(event); // Trigger an immediate scrub
  };

  const handleScrub = (event) => {
    if (!isDragging) return;

    const vid = videoRef.current;
    const rect = scrubBarRef.current.getBoundingClientRect();
    const clickX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    const newTime = (clickX / rect.width) * vid.duration;

    if (vid) {
      vid.currentTime = newTime; // Directly update without GSAP for real-time scrubbing
      setProgress((newTime / vid.duration) * 100);
    }
  };

  const handleScrubEnd = () => {
    setIsDragging(false);
  };

  return (
    <>
      {isMobile ? (
        <div className="">
          <img src={imgFallback} alt="Mobile Fallback" />
        </div>
      ) : (
        <div
          className="video-container"
          ref={videoContainerRef}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <video ref={videoRef} src={videoUrlDT} muted />
          <div className="play-button" onClick={togglePlay}>
            {isPlaying ? <Pause /> : <Play />}
          </div>
          <div
            className={`scrub-bar ${hover ? "visible" : ""}`}
            ref={scrubBarRef}
            onMouseDown={handleScrubStart}
            onMouseMove={handleScrub}
            onMouseUp={handleScrubEnd}
          >
            <div
              className="progress"
              style={{
                width: `${progress}%`,
              }}
            ></div>
            <div
              className="scrub-handle"
              style={{
                left: `${progress}%`,
              }}
              onMouseDown={handleScrubStart}
              onMouseUp={handleScrubEnd}
            ></div>
          </div>
        </div>
      )}
    </>
  );
}

export default FXVideoPlayer;
